import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import FlightDetails from "./FlightResults/FlightDetails";
import {
  FaPlane,
  FaChevronDown,
  FaChevronUp,
  FaClock,
  FaChair,
  FaSuitcase,
  FaExchangeAlt,
  FaCheckCircle,
  FaSpinner,
  FaInfoCircle,
  FaUtensils,
  FaGlobe,
  FaWeightHanging,
  FaHamburger,
  FaPlusCircle,
  FaMinusCircle,
  FaTag,
} from "react-icons/fa";
import { processFareOptions, extractAirlineName } from "../utils/flightHelpers";
import { useCurrency } from "../context/CurrencyContext";
import { formatPriceSync } from "../utils/currency";
import { formatTimeAMPM } from "../utils/flightUtils";

const InternationalRoundTripFlightCard = ({
  flight,
  index,
  tripType,
  expandedFlights,
  toggleFlightDetails,
  setSelectedFlightForPrice,
  setShowPriceModal,
  verifyFlightPrice,
  fetchSSROptions,
  handleAuthCheck,
  statusRefdId,
  onwardRefID,
  returnRefId,
  formatApiDateTime,
  minutesToHm,
  getSegments,
  getStopsText,
  getTotalDurationMinutes,
  isExpanded,
  setIsExpanded,
  onwardFareOptions,
  setOnwardFareOptions,
  returnFareOptions,
  setReturnFareOptions,
  loadingOnwardFares,
  setLoadingOnwardFares,
  loadingReturnFares,
  setLoadingReturnFares,
  selectedOnwardFare,
  setSelectedOnwardFare,
  selectedReturnFare,
  setSelectedReturnFare,
  showOnwardFares,
  setShowOnwardFares,
  showReturnFares,
  setShowReturnFares,
  expandedOnwardDetails,
  setExpandedOnwardDetails,
  expandedReturnDetails,
  setExpandedReturnDetails,
}) => {
  const { currency } = useCurrency();
  // Extract data from international flight structure
  const onwardFlight = flight?.Flights?.Onward;
  const returnFlight = flight?.Flights?.Return;

  // Extract SSR info
  const onwardSSRInfo = flight?.ssrInfo?.Onward;
  const returnSSRInfo = flight?.ssrInfo?.Return;

  // State for combined fares
  const [combinedFareOptions, setCombinedFareOptions] = useState(null);
  const [loadingCombinedFares, setLoadingCombinedFares] = useState(false);
  const [selectedCombinedFare, setSelectedCombinedFare] = useState(null);
  const [showCombinedFares, setShowCombinedFares] = useState(false);
  const [showSSRInfo, setShowSSRInfo] = useState(false);
  const [selectedSSRItems, setSelectedSSRItems] = useState({
    onward: {},
    return: {},
  });
  // V2: Separate refs for onward/return (like domestic) so FTD gets two refs for international round trip
  const [onwardBookingRefID, setOnwardBookingRefID] = useState(null);
  const [returnBookingRefID, setReturnBookingRefID] = useState(null);
  const [loadingSSR, setLoadingSSR] = useState(false);

  const getSegmentsFunc =
    getSegments ||
    ((leg) => {
      if (!leg) return [];
      if (Array.isArray(leg)) return leg.filter((s) => s && s.flightNo);
      if (typeof leg === "object") {
        const values = Object.values(leg);
        return values.filter((s) => s && s.flightNo);
      }
      return [];
    });

  const getStopsTextFunc =
    getStopsText ||
    ((leg) => {
      const segs = getSegmentsFunc(leg);
      const stops = Math.max(0, segs.length - 1);
      if (stops === 0) return "Non-stop";
      const vias = segs
        .slice(0, -1)
        .map((s) => s.arrCode)
        .join(", ");
      return `${stops} Stop${stops > 1 ? "s" : ""}${vias ? ` via ${vias}` : ""
        }`;
    });

  const getTotalDurationMinutesFunc =
    getTotalDurationMinutes ||
    ((leg) => {
      if (!leg) return 0;
      if (leg.durTotal) return Number(leg.durTotal);
      const segs = getSegmentsFunc(leg);
      return segs.reduce((sum, s) => sum + Number(s.duration || 0), 0);
    });

  const onwardSegments = getSegmentsFunc(onwardFlight);
  const returnSegments = getSegmentsFunc(returnFlight);

  const onwardFirstSegment = onwardSegments[0];
  const onwardLastSegment = onwardSegments[onwardSegments.length - 1];
  const returnFirstSegment = returnSegments[0];
  const returnLastSegment = returnSegments[returnSegments.length - 1];

  // Get airline names
  const onwardAirline = extractAirlineName(onwardSegments);
  const returnAirline = extractAirlineName(returnSegments);

  // Get total price from flight data
  const totalPrice = parseInt(flight?.Fare?.total?.total || 0);

  // Get baggage info
  const onwardBaggage =
    flight?.Fare?.Onward?.bagCkin?.replace("||", "") || "0 KG";
  const onwardCabin =
    flight?.Fare?.Onward?.bagCbin?.replace("||", "") || "0 KG";
  const returnBaggage =
    flight?.Fare?.Return?.bagCkin?.replace("||", "") || "0 KG";
  const returnCabin =
    flight?.Fare?.Return?.bagCbin?.replace("||", "") || "0 KG";

  // For international, we need to get flight IDs
  const onwardFlightId = onwardFirstSegment?.flightID;
  const returnFlightId = returnFirstSegment?.flightID;

  // V2: Get fare type information from flight data
  const onwardFareData = flight?.Fare?.Onward;
  const returnFareData = flight?.Fare?.Return;
  const onwardFareTypeDesc = onwardFareData?.fareTypeDesc || "";
  const returnFareTypeDesc = returnFareData?.fareTypeDesc || "";

  // V2: Get fare type badge helper function
  const getFareTypeBadge = (fareTypeDesc, popMsg = "") => {
    if (!fareTypeDesc) return null;
    const desc = String(fareTypeDesc).toLowerCase();
    const msg = String(popMsg || "").toLowerCase();

    if (desc.includes("student") || msg.includes("student")) {
      return { text: "Student", color: "bg-green-100 text-green-800" };
    }
    if (desc.includes("senior") || desc.includes("citizen") || msg.includes("senior")) {
      return { text: "Senior Citizen", color: "bg-purple-100 text-purple-800" };
    }
    if (desc.includes("defence") || msg.includes("defence")) {
      return { text: "Defence", color: "bg-blue-100 text-blue-800" };
    }
    if (desc.includes("sme") || msg.includes("sme")) {
      return { text: "SME", color: "bg-yellow-100 text-yellow-800" };
    }
    if (desc.includes("promo") || desc.includes("offer") || msg.includes("promo")) {
      return { text: "Promo", color: "bg-orange-100 text-orange-800" };
    }
    return null;
  };

  // Handle selecting SSR items
  const handleSelectSSRItem = (type, ssrItem, ssrType) => {
    setSelectedSSRItems((prev) => {
      const newSelected = { ...prev };
      const key = `${ssrType}-${ssrItem.baggID || ssrItem.mealID}`;

      if (newSelected[type][key]) {
        // Remove if already selected
        delete newSelected[type][key];
      } else {
        // Add to selected
        newSelected[type][key] = {
          ...ssrItem,
          ssrType: ssrType,
          flightType: type,
        };
      }

      return newSelected;
    });
  };

  // Calculate SSR total
  const calculateSSRTotal = () => {
    let total = 0;

    Object.values(selectedSSRItems.onward).forEach((item) => {
      total += parseFloat(item.baggAmt || item.mealAmt || 0);
    });

    Object.values(selectedSSRItems.return).forEach((item) => {
      total += parseFloat(item.baggAmt || item.mealAmt || 0);
    });

    return total;
  };

  // Calculate base price using selected fare package if available
  const currentBasePrice = selectedCombinedFare
    ? (typeof selectedCombinedFare.price === "number"
      ? selectedCombinedFare.price
      : parseInt(selectedCombinedFare.price || 0))
    : totalPrice;

  // Calculate total price including SSR
  const totalPriceWithSSR = currentBasePrice + calculateSSRTotal();
  // Handle viewing combined prices
  const handleViewCombinedPrices = async () => {
    if (showCombinedFares) {
      setShowCombinedFares(false);
      setShowSSRInfo(false);
      return;
    }

    setLoadingCombinedFares(true);
    try {
      // For international round trip, verify price for the complete package
      if (!onwardFlightId || !statusRefdId) {
        throw new Error("Missing flight ID or reference ID");
      }

      const result = await verifyFlightPrice(
        onwardFlightId,
        statusRefdId,
        true, // isRoundTrip = true for international
        flight
      );
      const fareOptions = Array.isArray(result) ? result : (result?.fareOptions || []);
      if (result && typeof result === "object" && !Array.isArray(result) && (result.onwardRefID != null || result.returnRefID != null)) {
        setOnwardBookingRefID(result.onwardRefID ?? statusRefdId);
        setReturnBookingRefID(result.returnRefID ?? statusRefdId);
      }

      // Process the API response for combined fares
      const processedFares = processFareOptions(
        fareOptions,
        flight, // Pass the full flight object
        "combined"
      );

      // If no fare options returned, create a default one using the total price
      if (processedFares.length === 0) {
        const oVal = onwardFareData?.validation || onwardFareData?.Validation || {};
        const rVal = returnFareData?.validation || returnFareData?.Validation || {};
        const mergedValidation = {
          gstInd: oVal.gstInd === "1" || rVal.gstInd === "1" ? "1" : oVal.gstInd === "2" || rVal.gstInd === "2" ? "2" : (oVal.gstInd ?? rVal.gstInd ?? "0"),
          allowFQT: oVal.allowFQT === "1" || rVal.allowFQT === "1" ? "1" : (oVal.allowFQT ?? rVal.allowFQT ?? "0"),
          freeMeal: oVal.freeMeal === "1" || rVal.freeMeal === "1" ? "1" : (oVal.freeMeal ?? rVal.freeMeal ?? "0"),
        };
        processedFares.push({
          id: "combined-default",
          name: "Standard Package",
          price: totalPrice,
          base: flight?.Fare?.total?.base || "0",
          tax: flight?.Fare?.total?.tax || "0",
          refundable:
            flight?.Fare?.Onward?.refundType === "P"
              ? "Partially Refundable*"
              : "Non-Refundable",
          baggage: `${onwardBaggage} / ${returnBaggage}`,
          cabin: `${onwardCabin} / ${returnCabin}`,
          dateChangeFee: "Contact Airline",
          meals: "As per airline policy",
          seats: "Standard",
          color: "purple",
          type: "combined",
          flightData: flight,
          apiFare: null,
          baggageDetail: {
            onward: onwardBaggage,
            return: returnBaggage,
          },
          ssrInfo: {
            onward: onwardSSRInfo,
            return: returnSSRInfo,
          },
          validation: mergedValidation,
        });
      }

      setCombinedFareOptions(processedFares);
      setShowCombinedFares(true);
    } catch (error) {
      console.error("Error fetching combined fare options:", error);
      // Fallback to default fares if API fails – include validation (GST, FQT) from flight Fare
      const oVal = onwardFareData?.validation || onwardFareData?.Validation || {};
      const rVal = returnFareData?.validation || returnFareData?.Validation || {};
      const mergedValidation = {
        gstInd: oVal.gstInd === "1" || rVal.gstInd === "1" ? "1" : oVal.gstInd === "2" || rVal.gstInd === "2" ? "2" : (oVal.gstInd ?? rVal.gstInd ?? "0"),
        allowFQT: oVal.allowFQT === "1" || rVal.allowFQT === "1" ? "1" : (oVal.allowFQT ?? rVal.allowFQT ?? "0"),
        freeMeal: oVal.freeMeal === "1" || rVal.freeMeal === "1" ? "1" : (oVal.freeMeal ?? rVal.freeMeal ?? "0"),
      };
      const defaultFares = [
        {
          id: "combined-retail",
          name: "Standard Package",
          price: totalPrice,
          refundable:
            flight?.Fare?.Onward?.refundType === "P"
              ? "Partially Refundable*"
              : "Non-Refundable",
          baggage: `${onwardBaggage} / ${returnBaggage}`,
          cabin: `${onwardCabin} / ${returnCabin}`,
          dateChangeFee: "Contact Airline",
          meals: "As per airline policy",
          seats: "Standard",
          color: "purple",
          type: "combined",
          flightData: flight,
          ssrInfo: {
            onward: onwardSSRInfo,
            return: returnSSRInfo,
          },
          validation: mergedValidation,
        },
      ];
      setCombinedFareOptions(defaultFares);
      setShowCombinedFares(true);
    } finally {
      setLoadingCombinedFares(false);
    }
  };

  // Handle selecting combined fare – fetch SSR for both onward and return (like one-way) so user can add baggage/meals
  const handleSelectCombinedFare = async (fare) => {
    setSelectedCombinedFare(fare);
    setShowCombinedFares(false);
    setSelectedSSRItems({ onward: {}, return: {} });

    const onwardRef = onwardRefID ?? onwardBookingRefID ?? statusRefdId;
    const returnRef = returnRefId ?? returnBookingRefID ?? statusRefdId;

    // Fetch SSR for whichever leg(s) we have flightID + ref (onward and/or return)
    const canFetchOnward = fetchSSROptions && onwardFlightId && onwardRef;
    const canFetchReturn = fetchSSROptions && returnFlightId && returnRef;

    // V2 FIX: Do not call fetchSSROptions again here with the new flightID.
    /*
    if (canFetchOnward || canFetchReturn) {
      setLoadingSSR(true);
      try {
        const [onwardSsr, returnSsr] = await Promise.all([
          canFetchOnward ? fetchSSROptions(onwardFlightId, onwardRef, false) : Promise.resolve({ ssrInfo: {} }),
          canFetchReturn ? fetchSSROptions(returnFlightId, returnRef, false) : Promise.resolve({ ssrInfo: {} }),
        ]);
        const onwardSSR = onwardSsr?.ssrInfo?.Onward ?? onwardSsr?.ssrInfo?.onward ?? {};
        const returnSSR = returnSsr?.ssrInfo?.Onward ?? returnSsr?.ssrInfo?.onward ?? {};
        setSelectedCombinedFare((prev) => ({
          ...prev,
          ssrInfo: { Onward: onwardSSR, Return: returnSSR },
          baggageOptions: onwardSSR.Bagg || onwardSSR.bagg || [],
          mealOptions: onwardSSR.Meal || onwardSSR.meal || [],
        }));
        setShowSSRInfo(true);
      } catch (e) {
        console.warn("SSR fetch for international round trip:", e);
        setShowSSRInfo(true);
      } finally {
        setLoadingSSR(false);
      }
    }
    */
    if (fare.ssrInfo) {
      setSelectedCombinedFare((prev) => ({ ...prev, ...fare }));
      setShowSSRInfo(true);
    } else {
      setSelectedCombinedFare((prev) => ({ ...prev, ...fare }));
      setShowSSRInfo(true);
    }
  };

  // Handle booking with combined fare and SSR items
  const handleBookNow = () => {
    if (!selectedCombinedFare) {
      alert("Please select a fare package to proceed with booking");
      return;
    }

    // Prepare SSR items for booking
    const ssrItems = {
      onward: Object.values(selectedSSRItems.onward),
      return: Object.values(selectedSSRItems.return),
    };

    // Prepare data for handleAuthCheck (pass onwardRefID + returnRefID so backend gets two refs like domestic)
    const flightWithRefs = {
      ...flight,
      onwardRefID: onwardBookingRefID ?? flight?.onwardRefID ?? statusRefdId,
      returnRefId: returnBookingRefID ?? flight?.returnRefId ?? statusRefdId,
    };
    const bookingData = {
      isRoundTrip: true,
      isInternational: true,
      flight: flightWithRefs, // Full flight object with onward/return refs for international round trip
      selectedFare: selectedCombinedFare,
      totalPrice: totalPriceWithSSR,
      basePrice: currentBasePrice,
      ssrTotal: calculateSSRTotal(),
      ssrItems: ssrItems,
      flightID: selectedCombinedFare?.apiFare?.flightID || selectedCombinedFare?.flightID || onwardFlightId,
      refID: onwardBookingRefID || statusRefdId,
      onwardFlight: {
        Flights: { Onward: onwardFlight },
        Fare: flight.Fare?.Onward,
        ssrInfo: onwardSSRInfo,
      },
      returnFlight: {
        Flights: { Onward: returnFlight },
        Fare: flight.Fare?.Return,
        ssrInfo: returnSSRInfo,
      },
    };

    handleAuthCheck(bookingData);
  };

  // Render SSR options section – same UI/UX as OneWayFlightCard (dropdowns, same styling)
  const renderSSROptions = () => {
    const fareSSRInfo = selectedCombinedFare?.ssrInfo;
    const effectiveOnwardSSR = fareSSRInfo?.Onward || onwardSSRInfo;
    const effectiveReturnSSR = fareSSRInfo?.Return || returnSSRInfo;

    const fareOnwardBaggage = selectedCombinedFare?.baggageOptions || effectiveOnwardSSR?.Bagg || effectiveOnwardSSR?.bagg || [];
    const fareOnwardMeals = selectedCombinedFare?.mealOptions || effectiveOnwardSSR?.Meal || effectiveOnwardSSR?.meal || [];
    const fareReturnBaggage = effectiveReturnSSR?.Bagg || effectiveReturnSSR?.bagg || [];
    const fareReturnMeals = effectiveReturnSSR?.Meal || effectiveReturnSSR?.meal || [];

    const hasOnwardSSR = fareOnwardBaggage.length > 0 || fareOnwardMeals.length > 0;
    const hasReturnSSR = fareReturnBaggage.length > 0 || fareReturnMeals.length > 0;
    const hasAnySSR = hasOnwardSSR || hasReturnSSR;

    const getOnwardSelectedBagId = () => {
      const key = Object.keys(selectedSSRItems.onward).find((k) => k.startsWith("baggage-"));
      return key ? key.replace("baggage-", "") : "";
    };
    const getOnwardSelectedMealId = () => {
      const key = Object.keys(selectedSSRItems.onward).find((k) => k.startsWith("meal-"));
      return key ? key.replace("meal-", "") : "";
    };
    const getReturnSelectedBagId = () => {
      const key = Object.keys(selectedSSRItems.return).find((k) => k.startsWith("baggage-"));
      return key ? key.replace("baggage-", "") : "";
    };
    const getReturnSelectedMealId = () => {
      const key = Object.keys(selectedSSRItems.return).find((k) => k.startsWith("meal-"));
      return key ? key.replace("meal-", "") : "";
    };

    return (
      <div className="mt-6 border-t border-gray-300 pt-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="bg-white rounded-lg border border-gray-300 p-4 shadow-sm">
          <div className="mb-3">
            <h4 className="text-sm font-bold text-gray-900 mb-1">
              Add-ons (Optional – Pay only if selected)
            </h4>
            <p className="text-xs text-gray-600">
              Enhance your journey with optional baggage and meals for each leg.
            </p>
          </div>

          {loadingSSR ? (
            <div className="flex items-center justify-center py-8 text-gray-500">
              <FaSpinner className="animate-spin mr-2" />
              Loading add-ons…
            </div>
          ) : !hasAnySSR ? (
            <div className="text-sm text-gray-500 py-4">
              No add-ons available for this fare.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Onward Add-ons – same as OneWayFlightCard */}
              {hasOnwardSSR && (
                <div className="p-3 rounded-lg border border-gray-200 bg-gray-50/50 space-y-3">
                  <h5 className="text-sm font-semibold text-gray-800 flex items-center">
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded mr-2">Onward</span>
                    Extra services
                  </h5>
                  {fareOnwardBaggage.length > 0 && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        <FaSuitcase className="inline mr-1" /> Baggage
                      </label>
                      <select
                        className="w-full text-sm border-2 border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        value={getOnwardSelectedBagId()}
                        onChange={(e) => {
                          const bag = e.target.value
                            ? fareOnwardBaggage.find((b) => b.baggID === e.target.value)
                            : null;
                          setSelectedSSRItems((prev) => {
                            const o = { ...prev.onward };
                            Object.keys(o).filter((k) => k.startsWith("baggage-")).forEach((k) => delete o[k]);
                            if (bag) o[`baggage-${bag.baggID}`] = bag;
                            return { ...prev, onward: o };
                          });
                        }}
                      >
                        <option value="">No extra baggage ({formatPriceSync(0, currency)})</option>
                        {fareOnwardBaggage.map((bag, idx) => (
                          <option key={idx} value={bag.baggID}>
                            {bag.baggDesc} (+{formatPriceSync(parseInt(bag.baggAmt || 0), currency)})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {fareOnwardMeals.length > 0 && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        <FaUtensils className="inline mr-1" /> Meal
                      </label>
                      <select
                        className="w-full text-sm border-2 border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        value={getOnwardSelectedMealId()}
                        onChange={(e) => {
                          const meal = e.target.value
                            ? fareOnwardMeals.find((m) => m.mealID === e.target.value)
                            : null;
                          setSelectedSSRItems((prev) => {
                            const o = { ...prev.onward };
                            Object.keys(o).filter((k) => k.startsWith("meal-")).forEach((k) => delete o[k]);
                            if (meal) o[`meal-${meal.mealID}`] = meal;
                            return { ...prev, onward: o };
                          });
                        }}
                      >
                        <option value="">No meal ({formatPriceSync(0, currency)})</option>
                        {fareOnwardMeals.map((meal, idx) => (
                          <option key={idx} value={meal.mealID}>
                            {meal.mealDesc} {meal.mealRef ? `(Seg ${meal.mealRef})` : ""} (+{formatPriceSync(parseFloat(meal.mealAmt || 0), currency)})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}
              {/* Return Add-ons – same as OneWayFlightCard */}
              {hasReturnSSR && (
                <div className="p-3 rounded-lg border border-gray-200 bg-gray-50/50 space-y-3">
                  <h5 className="text-sm font-semibold text-gray-800 flex items-center">
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded mr-2">Return</span>
                    Extra services
                  </h5>
                  {fareReturnBaggage.length > 0 && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        <FaSuitcase className="inline mr-1" /> Baggage
                      </label>
                      <select
                        className="w-full text-sm border-2 border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        value={getReturnSelectedBagId()}
                        onChange={(e) => {
                          const bag = e.target.value
                            ? fareReturnBaggage.find((b) => b.baggID === e.target.value)
                            : null;
                          setSelectedSSRItems((prev) => {
                            const r = { ...prev.return };
                            Object.keys(r).filter((k) => k.startsWith("baggage-")).forEach((k) => delete r[k]);
                            if (bag) r[`baggage-${bag.baggID}`] = bag;
                            return { ...prev, return: r };
                          });
                        }}
                      >
                        <option value="">No extra baggage ({formatPriceSync(0, currency)})</option>
                        {fareReturnBaggage.map((bag, idx) => (
                          <option key={idx} value={bag.baggID}>
                            {bag.baggDesc} (+{formatPriceSync(parseInt(bag.baggAmt || 0), currency)})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {fareReturnMeals.length > 0 && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        <FaUtensils className="inline mr-1" /> Meal
                      </label>
                      <select
                        className="w-full text-sm border-2 border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        value={getReturnSelectedMealId()}
                        onChange={(e) => {
                          const meal = e.target.value
                            ? fareReturnMeals.find((m) => m.mealID === e.target.value)
                            : null;
                          setSelectedSSRItems((prev) => {
                            const r = { ...prev.return };
                            Object.keys(r).filter((k) => k.startsWith("meal-")).forEach((k) => delete r[k]);
                            if (meal) r[`meal-${meal.mealID}`] = meal;
                            return { ...prev, return: r };
                          });
                        }}
                      >
                        <option value="">No meal ({formatPriceSync(0, currency)})</option>
                        {fareReturnMeals.map((meal, idx) => (
                          <option key={idx} value={meal.mealID}>
                            {meal.mealDesc} {meal.mealRef ? `(Seg ${meal.mealRef})` : ""} (+{formatPriceSync(parseFloat(meal.mealAmt || 0), currency)})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Selected SSR Summary */}
        {Object.keys(selectedSSRItems.onward).length > 0 ||
          Object.keys(selectedSSRItems.return).length > 0 ? (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-bold text-gray-900 mb-2">
              Selected Extra Services
            </h4>
            <div className="space-y-2">
              {Object.values(selectedSSRItems.onward).map((item, idx) => (
                <div
                  key={`selected-onward-${idx}`}
                  className="flex justify-between text-sm"
                >
                  <div className="flex items-center">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded mr-2">
                      Onward
                    </span>
                    <span>{item.baggDesc || item.mealDesc}</span>
                  </div>
                  <div className="font-medium">
                    ₹{parseFloat(item.baggAmt || item.mealAmt).toLocaleString()}
                  </div>
                </div>
              ))}
              {Object.values(selectedSSRItems.return).map((item, idx) => (
                <div
                  key={`selected-return-${idx}`}
                  className="flex justify-between text-sm"
                >
                  <div className="flex items-center">
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded mr-2">
                      Return
                    </span>
                    <span>{item.baggDesc || item.mealDesc}</span>
                  </div>
                  <div className="font-medium">
                    ₹{parseFloat(item.baggAmt || item.mealAmt).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-blue-300 flex justify-between font-bold">
              <span>Extra Services Total:</span>
              <span className="text-purple-700">
                ₹{calculateSSRTotal().toLocaleString()}
              </span>
            </div>
          </div>
        ) : (
          showSSRInfo && (
            <div className="text-center py-4 text-gray-500">
              Select extra services to add to your booking
            </div>
          )
        )}
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden mb-6 hover:shadow-xl transition-shadow duration-300"
    >
      {/* Round Trip Header - International - Responsive */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
          <div className="flex items-center space-x-2 sm:space-x-3 w-full sm:w-auto">
            <div className="bg-purple-100 p-1.5 sm:p-2 rounded-full flex-shrink-0">
              <FaGlobe className="text-purple-600 text-sm sm:text-base" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1">
                <h3 className="text-sm sm:text-lg font-bold text-gray-900">
                  International Round Trip
                </h3>
                {/* V2: Display fare type badges */}
                {onwardFareTypeDesc && getFareTypeBadge(onwardFareTypeDesc, onwardFareData?.popMsg) && (
                  <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-semibold rounded flex-shrink-0 ${getFareTypeBadge(onwardFareTypeDesc, onwardFareData?.popMsg).color
                    }`}>
                    {getFareTypeBadge(onwardFareTypeDesc, onwardFareData?.popMsg).text}
                  </span>
                )}
                {returnFareTypeDesc && getFareTypeBadge(returnFareTypeDesc, returnFareData?.popMsg) && (
                  <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-semibold rounded flex-shrink-0 ${getFareTypeBadge(returnFareTypeDesc, returnFareData?.popMsg).color
                    }`}>
                    {getFareTypeBadge(returnFareTypeDesc, returnFareData?.popMsg).text}
                  </span>
                )}
              </div>
              <p className="text-sm sm:text-lg font-bold text-gray-600 truncate">
                {onwardAirline} / {returnAirline}
              </p>
            </div>
          </div>
          <div className="text-left sm:text-right w-full sm:w-auto flex items-center justify-between sm:block">
            <div className="text-lg sm:text-xl font-bold text-purple-700">
              {formatPriceSync(totalPrice, currency)}
            </div>
            <div className="text-xs sm:text-sm text-gray-500">Total Package</div>
          </div>
        </div>
      </div>

      <div className="p-3 sm:p-6">
        {/* Flights Comparison – clear Onward vs Return separation (like one-way / domestic round trip) */}
        <div className="grid md:grid-cols-2 gap-3 sm:gap-6 mb-4 sm:mb-6">
          {/* ONWARD FLIGHT – clear section - Responsive */}
          <div className="border-2 border-gray-200 border-l-4 border-l-blue-500 rounded-xl p-3 sm:p-4 bg-white hover:shadow-md transition-shadow">
            <div className="mb-2 sm:mb-3 pb-2 border-b border-blue-100">
              <span className="text-xs sm:text-sm font-bold text-blue-800 uppercase tracking-wide">Onward Flight</span>
              <span className="text-xs text-gray-600 ml-1 sm:ml-2 hidden sm:inline">
                {formatApiDateTime ? formatApiDateTime(onwardFirstSegment?.depDate)?.split(",")[0] : onwardFirstSegment?.depDate?.slice(6, 8) + "/" + onwardFirstSegment?.depDate?.slice(4, 6)}
              </span>
            </div>
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-xs text-gray-500">
                    {formatApiDateTime
                      ? formatApiDateTime(onwardFirstSegment?.depDate)?.split(
                        ","
                      )[0]
                      : onwardFirstSegment?.depDate?.slice(6, 8) +
                      "/" +
                      onwardFirstSegment?.depDate?.slice(4, 6)}
                  </span>
                </div>
                <h4 className="font-semibold text-gray-800">{onwardAirline}</h4>
              </div>
            </div>

            {/* Flight Route */}
            <div className="flex items-center justify-between mb-3">
              <div className="text-center">
                <div className="text-lg font-bold">
                  {onwardFirstSegment?.depCode}
                </div>
                <div className="text-sm text-gray-500">
                  {formatTimeAMPM(onwardFirstSegment?.depDate, onwardFirstSegment?.depTime)}
                </div>
              </div>

              <div className="flex-1 px-4">
                <div className="relative">
                  <div className="flex items-center">
                    <div className="flex-1 border-t border-gray-300"></div>
                    <div className="mx-2 relative">
                      <FaPlane className="text-blue-500 text-sm" />
                      <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 text-xs text-gray-500 whitespace-nowrap">
                        {minutesToHm
                          ? minutesToHm(
                            getTotalDurationMinutesFunc(onwardFlight)
                          )
                          : onwardFlight?.durTotal
                            ? minutesToHm(onwardFlight.durTotal)
                            : "N/A"}
                      </div>
                    </div>
                    <div className="flex-1 border-t border-gray-300"></div>
                  </div>
                  <div className="text-center text-xs text-gray-600 mt-1">
                    {getStopsTextFunc(onwardFlight) || "Non-stop"}
                  </div>
                </div>
              </div>

              <div className="text-center">
                <div className="text-lg font-bold">
                  {onwardLastSegment?.arrCode}
                </div>
                <div className="text-sm text-gray-500">
                  {formatTimeAMPM(onwardLastSegment?.arrDate, onwardLastSegment?.arrTime)}
                </div>
              </div>
            </div>

            {/* Flight Info */}
            <div className="flex items-center text-sm text-gray-600 mb-4">
              <FaClock className="mr-1 text-gray-400" />
              <span>
                {minutesToHm
                  ? minutesToHm(getTotalDurationMinutesFunc(onwardFlight))
                  : onwardFlight?.durTotal
                    ? minutesToHm(onwardFlight.durTotal)
                    : "N/A"}
              </span>
              <span className="mx-2">•</span>
              <span>{onwardFirstSegment?.flightNo}</span>
              <span className="mx-2">•</span>
              <span className="flex items-center">
                <FaChair className="mr-1 text-gray-400" />
                Cabin: {onwardCabin}
              </span>
            </div>

            {/* Baggage Info */}
            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center text-sm">
                <FaSuitcase className="mr-2 text-blue-500" />
                <span className="font-medium">Baggage Allowance:</span>
                <span className="ml-2">{onwardBaggage} Check-in</span>
              </div>
              {onwardSSRInfo?.Bagg?.length > 0 && (
                <div className="mt-2 text-xs text-gray-600">
                  <FaInfoCircle className="inline mr-1 text-blue-400" />
                  Extra baggage options available
                </div>
              )}
            </div>

            {/* Onward Flight Details Toggle */}
            <div className="border-t pt-3 mt-3">
              <button
                onClick={() => setExpandedOnwardDetails(!expandedOnwardDetails)}
                className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium w-full justify-end"
              >
                {expandedOnwardDetails ? (
                  <>
                    Hide Flight Details <FaChevronUp className="ml-1" />
                  </>
                ) : (
                  <>
                    View Flight Details <FaChevronDown className="ml-1" />
                  </>
                )}
              </button>
            </div>

            {/* Onward Flight Details */}
            <AnimatePresence>
              {expandedOnwardDetails && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-4 border-t pt-4"
                >
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    Onward Flight Details
                  </h4>
                  <FlightDetails segments={onwardSegments} flight={flight} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* RETURN FLIGHT – clear section */}
          <div className="border-2 border-gray-200 border-l-4 border-l-green-500 rounded-xl p-4 bg-white hover:shadow-md transition-shadow">
            <div className="mb-3 pb-2 border-b border-green-100">
              <span className="text-sm font-bold text-green-800 uppercase tracking-wide">Return Flight</span>
              <span className="text-xs text-gray-600 ml-2">
                {formatApiDateTime ? formatApiDateTime(returnFirstSegment?.depDate)?.split(",")[0] : returnFirstSegment?.depDate?.slice(6, 8) + "/" + returnFirstSegment?.depDate?.slice(4, 6)}
              </span>
            </div>
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-xs text-gray-500">
                    {formatApiDateTime ? formatApiDateTime(returnFirstSegment?.depDate)?.split(",")[0] : returnFirstSegment?.depDate?.slice(6, 8) + "/" + returnFirstSegment?.depDate?.slice(4, 6)}
                  </span>
                </div>
                <h4 className="font-semibold text-gray-800">{returnAirline}</h4>
              </div>
            </div>

            {/* Flight Route */}
            <div className="flex items-center justify-between mb-3">
              <div className="text-center">
                <div className="text-lg font-bold">
                  {returnFirstSegment?.depCode}
                </div>
                <div className="text-sm text-gray-500">
                  {formatApiDateTime
                    ? formatApiDateTime(returnFirstSegment?.depDate)
                      ?.split(",")[1]
                      ?.trim()
                    : returnFirstSegment?.depDate?.slice(8, 10) +
                    ":" +
                    returnFirstSegment?.depDate?.slice(10, 12)}
                </div>
              </div>

              <div className="flex-1 px-4">
                <div className="relative">
                  <div className="flex items-center">
                    <div className="flex-1 border-t border-gray-300"></div>
                    <div className="mx-2 relative">
                      <FaPlane className="text-green-500 text-sm" />
                      <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 text-xs text-gray-500 whitespace-nowrap">
                        {minutesToHm
                          ? minutesToHm(
                            getTotalDurationMinutesFunc(returnFlight)
                          )
                          : returnFlight?.durTotal
                            ? minutesToHm(returnFlight.durTotal)
                            : "N/A"}
                      </div>
                    </div>
                    <div className="flex-1 border-t border-gray-300"></div>
                  </div>
                  <div className="text-center text-xs text-gray-600 mt-1">
                    {getStopsTextFunc(returnFlight) || "Non-stop"}
                  </div>
                </div>
              </div>

              <div className="text-center">
                <div className="text-lg font-bold">
                  {returnLastSegment?.arrCode}
                </div>
                <div className="text-sm text-gray-500">
                  {formatApiDateTime
                    ? formatApiDateTime(returnLastSegment?.arrDate)
                      ?.split(",")[1]
                      ?.trim()
                    : returnLastSegment?.arrDate?.slice(8, 10) +
                    ":" +
                    returnLastSegment?.arrDate?.slice(10, 12)}
                </div>
              </div>
            </div>

            {/* Flight Info */}
            <div className="flex items-center text-sm text-gray-600 mb-4">
              <FaClock className="mr-1 text-gray-400" />
              <span>
                {minutesToHm
                  ? minutesToHm(getTotalDurationMinutesFunc(returnFlight))
                  : returnFlight?.durTotal
                    ? minutesToHm(returnFlight.durTotal)
                    : "N/A"}
              </span>
              <span className="mx-2">•</span>
              <span>{returnFirstSegment?.flightNo}</span>
              <span className="mx-2">•</span>
              <span className="flex items-center">
                <FaChair className="mr-1 text-gray-400" />
                Cabin: {returnCabin}
              </span>
            </div>

            {/* Baggage Info */}
            <div className="mb-4 p-3 bg-green-50 rounded-lg">
              <div className="flex items-center text-sm">
                <FaSuitcase className="mr-2 text-green-500" />
                <span className="font-medium">Baggage Allowance:</span>
                <span className="ml-2">{returnBaggage} Check-in</span>
              </div>
              {returnSSRInfo?.Bagg?.length > 0 && (
                <div className="mt-2 text-xs text-gray-600">
                  <FaInfoCircle className="inline mr-1 text-green-400" />
                  Extra baggage options available
                </div>
              )}
            </div>

            {/* Return Flight Details Toggle */}
            <div className="border-t pt-3 mt-3">
              <button
                onClick={() => setExpandedReturnDetails(!expandedReturnDetails)}
                className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium w-full justify-end"
              >
                {expandedReturnDetails ? (
                  <>
                    Hide Flight Details <FaChevronUp className="ml-1" />
                  </>
                ) : (
                  <>
                    View Flight Details <FaChevronDown className="ml-1" />
                  </>
                )}
              </button>
            </div>

            {/* Return Flight Details */}
            <AnimatePresence>
              {expandedReturnDetails && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-4 border-t pt-4"
                >
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    Return Flight Details
                  </h4>
                  <FlightDetails segments={returnSegments} flight={flight} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Divider: Onward vs Return vs Pricing – clear separation */}
        <div className="h-1 bg-gradient-to-r from-blue-200 via-purple-200 to-green-200 mb-6" aria-hidden="true" />

        {/* SINGLE VIEW PRICES BUTTON - BELOW BOTH FLIGHTS */}
        <div className="flex justify-center mb-4 sm:mb-6 px-3 sm:px-0">
          <button
            onClick={handleViewCombinedPrices}
            className="w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm sm:text-base font-semibold rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-colors flex items-center justify-center space-x-2 min-h-[44px] sm:min-h-0"
          >
            {loadingCombinedFares ? (
              <>
                <FaSpinner className="animate-spin" />
                <span>Loading Prices...</span>
              </>
            ) : showCombinedFares ? (
              <>
                <span className="hidden sm:inline">Hide Prices</span>
                <span className="sm:hidden">Hide</span>
              </>
            ) : (
              <>
                <span className="hidden sm:inline">View Prices & Book</span>
                <span className="sm:hidden">View Prices</span>
              </>
            )}
          </button>
        </div>

        {/* Pricing Options – one-way style table (Base Fare Type, Baggage, Services, Price) */}
        <AnimatePresence>
          {showCombinedFares && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="mt-4 mb-6 border-t border-gray-200 pt-6"
            >
              <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
                <div className="px-4 py-3 bg-gradient-to-r from-purple-50 to-indigo-50 border-b-2 border-gray-200">
                  <h4 className="text-base font-bold text-gray-900 mb-1">Pricing options (Onward + Return)</h4>
                  <p className="text-xs text-gray-600">Select a fare package – same format as one-way</p>
                </div>
                {/* Table header – Hidden on mobile, shown on md+ */}
                <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 bg-gradient-to-r from-purple-50 to-indigo-50 text-xs font-bold text-gray-800 border-b-2 border-gray-200">
                  <div className="col-span-3 flex items-center">
                    <FaTag className="mr-2 text-purple-600" />
                    Base Fare Type
                  </div>
                  <div className="col-span-2 flex items-center">
                    <FaSuitcase className="mr-2 text-purple-600" />
                    Included Baggage
                  </div>
                  <div className="col-span-2 flex items-center">
                    <FaUtensils className="mr-2 text-purple-600" />
                    Included Services
                  </div>
                  <div className="col-span-5 flex items-center justify-end">
                    <span className="text-purple-600 font-semibold">₹</span> Base Price / Select
                  </div>
                </div>
                {loadingCombinedFares ? (
                  <div className="text-center py-8">
                    <FaSpinner className="animate-spin text-purple-500 text-xl mx-auto" />
                    <p className="text-gray-500 mt-2 text-sm">Loading fare options...</p>
                  </div>
                ) : combinedFareOptions?.length > 0 ? (
                  <div className="divide-y divide-gray-100">
                    {combinedFareOptions.map((fare, idx) => {
                      const isSelected = selectedCombinedFare?.id === fare.id;
                      const baggageOnward = fare.baggageDetail?.onward ?? onwardBaggage ?? "—";
                      const baggageReturn = fare.baggageDetail?.return ?? returnBaggage ?? "—";
                      return (
                        <div
                          key={fare.id || idx}
                          className={`md:grid md:grid-cols-12 md:gap-2 px-3 sm:px-4 py-3 sm:py-4 hover:bg-purple-50/50 transition-colors cursor-pointer border-b border-gray-100 last:border-b-0 ${isSelected ? "bg-purple-50 border-l-4 border-purple-500 md:border-l-4" : ""
                            }`}
                          onClick={() => handleSelectCombinedFare(fare)}
                        >
                          {/* Mobile Layout - Stacked */}
                          <div className="md:hidden space-y-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-1.5 mb-1">
                                  <span className="text-sm font-semibold text-gray-900">
                                    {fare.name || fare.fareTypeDesc || `Fare ${idx + 1}`}
                                  </span>
                                  {fare.fareTypeDesc && getFareTypeBadge(fare.fareTypeDesc, fare.popMsg) && (
                                    <span className={`px-1.5 py-0.5 text-xs rounded font-medium flex-shrink-0 ${getFareTypeBadge(fare.fareTypeDesc, fare.popMsg).color}`}>
                                      {getFareTypeBadge(fare.fareTypeDesc, fare.popMsg).text}
                                    </span>
                                  )}
                                </div>
                                <div className="text-lg font-bold text-gray-900 mb-1">
                                  {formatPriceSync(typeof fare.price === "number" ? fare.price : parseInt(fare.price) || 0, currency)}
                                </div>
                                <div className="text-xs text-gray-500">Total package • Total Base Fare</div>
                              </div>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleSelectCombinedFare(fare); }}
                                className={`px-3 py-2 rounded text-xs font-semibold flex-shrink-0 ${isSelected ? "bg-green-600 text-white" : "bg-purple-600 text-white hover:bg-purple-700"}`}
                              >
                                {isSelected ? "Selected" : "Select"}
                              </button>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-xs">
                              <div>
                                <div className="font-semibold text-gray-700 mb-1">Baggage</div>
                                <div className="text-gray-600">
                                  <div>Onward: {baggageOnward}</div>
                                  <div className="mt-0.5">Return: {baggageReturn}</div>
                                  {(fare.cabin || onwardCabin) && (
                                    <div className="text-gray-500 mt-0.5">Cabin: {onwardCabin} / {returnCabin}</div>
                                  )}
                                </div>
                              </div>
                              <div>
                                <div className="font-semibold text-gray-700 mb-1">Services</div>
                                <div className="space-y-0.5 text-gray-600">
                                  <div>Date Change: {fare.dateChangeFee || "—"}</div>
                                  <div className={fare.meals === "Available" || fare.validation?.freeMeal === "1" ? "text-green-600" : "text-gray-600"}>
                                    Meal: {fare.meals === "Available" || fare.validation?.freeMeal === "1" ? "Free" : "Paid"}
                                  </div>
                                  {fare.validation?.gstInd !== undefined && (
                                    <div className={fare.validation.gstInd === "1" ? "text-amber-700 font-medium" : fare.validation.gstInd === "2" ? "text-blue-600" : "text-gray-600"}>
                                      GST: {fare.validation.gstInd === "1" ? "Mandatory" : fare.validation.gstInd === "2" ? "Optional" : "Not Allowed"}
                                    </div>
                                  )}
                                  {fare.validation?.allowFQT !== undefined && (
                                    <div className="text-gray-600">FQT: {fare.validation.allowFQT === "1" ? "Allowed" : "Not Allowed"}</div>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className={`text-xs ${fare.refundable?.toLowerCase?.().includes("non") ? "text-red-600 font-medium" : "text-green-600 font-medium"}`}>
                              {fare.refundable || "Partially Refundable"}
                            </div>
                            {fare.popMsg && <div className="text-xs text-blue-600 italic">{fare.popMsg}</div>}
                          </div>

                          {/* Desktop Layout - Grid */}
                          <div className="hidden md:block col-span-3">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-semibold text-gray-900">
                                {fare.name || fare.fareTypeDesc || `Fare ${idx + 1}`}
                              </span>
                              {fare.fareTypeDesc && getFareTypeBadge(fare.fareTypeDesc, fare.popMsg) && (
                                <span className={`px-1.5 py-0.5 text-xs rounded font-medium ${getFareTypeBadge(fare.fareTypeDesc, fare.popMsg).color}`}>
                                  {getFareTypeBadge(fare.fareTypeDesc, fare.popMsg).text}
                                </span>
                              )}
                            </div>
                            <div className={`text-xs ${fare.refundable?.toLowerCase?.().includes("non") ? "text-red-600" : "text-green-600"}`}>
                              {fare.refundable || "Partially Refundable"}
                            </div>
                            {fare.popMsg && <div className="text-xs text-blue-600 mt-0.5 italic">{fare.popMsg}</div>}
                          </div>
                          <div className="hidden md:block col-span-2 text-xs text-gray-700">
                            <div className="font-medium">Onward: {baggageOnward}</div>
                            <div className="text-gray-500 mt-0.5">Return: {baggageReturn}</div>
                            {(fare.cabin || onwardCabin) && (
                              <div className="text-gray-500 mt-0.5">Cabin: {onwardCabin} / {returnCabin}</div>
                            )}
                          </div>
                          <div className="hidden md:block col-span-2 text-xs text-gray-700">
                            <div>Date Change: {fare.dateChangeFee || "—"}</div>
                            <div className={`mt-0.5 ${fare.meals === "Available" || fare.validation?.freeMeal === "1" ? "text-green-600" : "text-gray-600"}`}>
                              Meal – {fare.meals === "Available" || fare.validation?.freeMeal === "1" ? "Free" : "Paid"}
                            </div>
                            {fare.validation?.gstInd !== undefined && (
                              <div className={`mt-0.5 font-medium ${fare.validation.gstInd === "1" ? "text-amber-700" : fare.validation.gstInd === "2" ? "text-blue-600" : "text-gray-600"
                                }`}>
                                GST – {fare.validation.gstInd === "1" ? "Mandatory" : fare.validation.gstInd === "2" ? "Optional" : "Not Allowed"}
                              </div>
                            )}
                            {fare.validation?.allowFQT !== undefined && (
                              <div className="mt-0.5 text-gray-600">FQT – {fare.validation.allowFQT === "1" ? "Allowed" : "Not Allowed"}</div>
                            )}
                          </div>
                          <div className="hidden md:flex col-span-5 items-center justify-end gap-3">
                            <div className="text-right">
                              <div className="text-lg font-bold text-gray-900">
                                {formatPriceSync(typeof fare.price === "number" ? fare.price : parseInt(fare.price) || 0, currency)}
                              </div>
                              <div className="text-xs text-gray-500">Total package • Total Base Fare</div>
                            </div>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleSelectCombinedFare(fare); }}
                              className={`px-3 py-1.5 rounded text-xs font-semibold ${isSelected ? "bg-green-600 text-white" : "bg-purple-600 text-white hover:bg-purple-700"}`}
                            >
                              {isSelected ? "Selected" : "Select Base Fare"}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 text-sm">No fare options available</div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* SSR Options Section */}
        {selectedCombinedFare && renderSSROptions()}

        {/* Combined Booking Section */}
        {selectedCombinedFare && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6"
          >
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    International Round Trip Summary
                  </h3>
                  <p className="text-gray-600">Review your selected package</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-purple-700">
                    {formatPriceSync(totalPriceWithSSR, currency)}
                  </div>
                  <div className="text-sm text-gray-500">
                    {calculateSSRTotal() > 0 && (
                      <div className="text-xs">
                        <span>Base: {formatPriceSync(totalPrice, currency)}</span>
                        <span className="mx-1">+</span>
                        <span>
                          Extras: {formatPriceSync(calculateSSRTotal(), currency)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-6">
                {/* Onward Summary – clear block (like domestic round trip) */}
                <div className="rounded-lg border-2 border-blue-200 bg-blue-50/50 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-1 bg-blue-600 text-white text-xs font-bold rounded uppercase">Onward</span>
                    <span className="font-medium text-gray-800">{onwardAirline}</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    {onwardFirstSegment?.depCode} → {onwardLastSegment?.arrCode}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Flight: {onwardFirstSegment?.flightNo} • Duration:{" "}
                    {minutesToHm ? minutesToHm(getTotalDurationMinutesFunc(onwardFlight)) : onwardFlight?.durTotal ? minutesToHm(onwardFlight.durTotal) : "N/A"}
                  </div>
                  <div className="mt-2 text-sm flex items-center text-gray-700">
                    <FaSuitcase className="mr-2 text-blue-500" />
                    {onwardBaggage} Check-in • Cabin: {onwardCabin}
                  </div>
                </div>

                {/* Return Summary – clear block (like domestic round trip) */}
                <div className="rounded-lg border-2 border-green-200 bg-green-50/50 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-1 bg-green-600 text-white text-xs font-bold rounded uppercase">Return</span>
                    <span className="font-medium text-gray-800">{returnAirline}</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    {returnFirstSegment?.depCode} → {returnLastSegment?.arrCode}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Flight: {returnFirstSegment?.flightNo} • Duration:{" "}
                    {minutesToHm ? minutesToHm(getTotalDurationMinutesFunc(returnFlight)) : returnFlight?.durTotal ? minutesToHm(returnFlight.durTotal) : "N/A"}
                  </div>
                  <div className="mt-2 text-sm flex items-center text-gray-700">
                    <FaSuitcase className="mr-2 text-green-500" />
                    {returnBaggage} Check-in • Cabin: {returnCabin}
                  </div>
                </div>
              </div>

              {/* Selected Package Info */}
              <div className="bg-white rounded-lg p-4 border mb-6">
                <div className="font-medium text-gray-700 mb-2">
                  Selected Package
                </div>
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <div className="font-bold text-gray-900">
                      {selectedCombinedFare.name}
                    </div>
                    <div className="text-sm text-gray-600">
                      {selectedCombinedFare.refundable}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-purple-700">
                      ₹{totalPrice.toLocaleString()}
                    </div>
                  </div>
                </div>

                {/* Selected SSR Items */}
                {calculateSSRTotal() > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="font-medium text-gray-700 mb-2">
                      Extra Services
                    </div>
                    {Object.values(selectedSSRItems.onward).map((item, idx) => (
                      <div
                        key={`summary-onward-${idx}`}
                        className="flex justify-between text-sm mb-1"
                      >
                        <div>
                          <span className="px-1 py-0.5 bg-blue-100 text-blue-700 text-xs rounded mr-1">
                            Onward
                          </span>
                          {item.baggDesc || item.mealDesc}
                        </div>
                        <div className="font-medium">
                          ₹
                          {parseFloat(
                            item.baggAmt || item.mealAmt
                          ).toLocaleString()}
                        </div>
                      </div>
                    ))}
                    {Object.values(selectedSSRItems.return).map((item, idx) => (
                      <div
                        key={`summary-return-${idx}`}
                        className="flex justify-between text-sm mb-1"
                      >
                        <div>
                          <span className="px-1 py-0.5 bg-green-100 text-green-700 text-xs rounded mr-1">
                            Return
                          </span>
                          {item.baggDesc || item.mealDesc}
                        </div>
                        <div className="font-medium">
                          ₹
                          {parseFloat(
                            item.baggAmt || item.mealAmt
                          ).toLocaleString()}
                        </div>
                      </div>
                    ))}
                    <div className="flex justify-between font-bold mt-2 pt-2 border-t">
                      <span>Extra Services Total:</span>
                      <span>{formatPriceSync(calculateSSRTotal(), currency)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Book Now Button */}
              <div className="flex justify-center">
                <button
                  onClick={handleBookNow}
                  className="px-12 py-4 rounded-lg font-bold text-xl transition-all duration-300 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  BOOK NOW - {formatPriceSync(totalPriceWithSSR, currency)}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default InternationalRoundTripFlightCard;

// import { useState, useEffect } from "react";
// import { motion, AnimatePresence } from "framer-motion";
// import FlightDetails from "./FlightResults/FlightDetails";
// import {
//   FaPlane,
//   FaChevronDown,
//   FaChevronUp,
//   FaClock,
//   FaChair,
//   FaSuitcase,
//   FaExchangeAlt,
//   FaCheckCircle,
//   FaSpinner,
//   FaInfoCircle,
//   FaUtensils,
//   FaGlobe,
//   FaWeightHanging,
//   FaHamburger,
//   FaPlusCircle,
//   FaMinusCircle,
//   FaTag,
// } from "react-icons/fa";

// const InternationalRoundTripFlightCard = ({
//   flight,
//   index,
//   tripType,
//   expandedFlights,
//   toggleFlightDetails,
//   setSelectedFlightForPrice,
//   setShowPriceModal,
//   verifyFlightPrice,
//   handleAuthCheck,
//   statusRefdId,
//   formatApiDateTime,
//   minutesToHm,
//   getSegments,
//   getStopsText,
//   getTotalDurationMinutes,
//   isExpanded,
//   setIsExpanded,
//   onwardFareOptions,
//   setOnwardFareOptions,
//   returnFareOptions,
//   setReturnFareOptions,
//   loadingOnwardFares,
//   setLoadingOnwardFares,
//   loadingReturnFares,
//   setLoadingReturnFares,
//   selectedOnwardFare,
//   setSelectedOnwardFare,
//   selectedReturnFare,
//   setSelectedReturnFare,
//   showOnwardFares,
//   setShowOnwardFares,
//   showReturnFares,
//   setShowReturnFares,
//   expandedOnwardDetails,
//   setExpandedOnwardDetails,
//   expandedReturnDetails,
//   setExpandedReturnDetails,
// }) => {
//   // Extract data from international flight structure
//   const onwardFlight = flight?.Flights?.Onward;
//   const returnFlight = flight?.Flights?.Return;

//   // State for fare options
//   const [fareOptions, setFareOptions] = useState([]);
//   const [loadingPrices, setLoadingPrices] = useState(false);
//   const [showPriceOptions, setShowPriceOptions] = useState(false);
//   const [selectedFare, setSelectedFare] = useState(null);
//   const [expandedSSRSections, setExpandedSSRSections] = useState({
//     onward: false,
//     return: false,
//   });
//   const [selectedSSROptions, setSelectedSSROptions] = useState({
//     onward: { baggage: null, meal: null },
//     return: { baggage: null, meal: null },
//   });

//   const getSegmentsFunc =
//     getSegments ||
//     ((leg) => {
//       if (!leg) return [];
//       if (Array.isArray(leg)) return leg.filter((s) => s && s.flightNo);
//       if (typeof leg === "object") {
//         const values = Object.values(leg);
//         return values.filter((s) => s && s.flightNo);
//       }
//       return [];
//     });

//   const getStopsTextFunc =
//     getStopsText ||
//     ((leg) => {
//       const segs = getSegmentsFunc(leg);
//       const stops = Math.max(0, segs.length - 1);
//       if (stops === 0) return "Non-stop";
//       const vias = segs
//         .slice(0, -1)
//         .map((s) => s.arrCode)
//         .join(", ");
//       return `${stops} Stop${stops > 1 ? "s" : ""}${
//         vias ? ` via ${vias}` : ""
//       }`;
//     });

//   const getTotalDurationMinutesFunc =
//     getTotalDurationMinutes ||
//     ((leg) => {
//       if (!leg) return 0;
//       if (leg.durTotal) return Number(leg.durTotal);
//       const segs = getSegmentsFunc(leg);
//       return segs.reduce((sum, s) => sum + Number(s.duration || 0), 0);
//     });

//   const onwardSegments = getSegmentsFunc(onwardFlight);
//   const returnSegments = getSegmentsFunc(returnFlight);

//   const onwardFirstSegment = onwardSegments[0];
//   const onwardLastSegment = onwardSegments[onwardSegments.length - 1];
//   const returnFirstSegment = returnSegments[0];
//   const returnLastSegment = returnSegments[returnSegments.length - 1];

//   // Get airline names
//   const extractAirlineName = (segments) => {
//     if (!segments || segments.length === 0) return "Multiple Airlines";
//     const airlines = [
//       ...new Set(segments.map((s) => s.airName || s.airCodeOp)),
//     ];
//     return airlines.join(" / ");
//   };

//   const onwardAirline = extractAirlineName(onwardSegments);
//   const returnAirline = extractAirlineName(returnSegments);

//   // Get total price from flight data
//   const totalPrice = parseInt(flight?.Fare?.total?.total || 0);
//   const basePrice = parseInt(flight?.Fare?.total?.base || 0);
//   const taxPrice = parseInt(flight?.Fare?.total?.tax || 0);

//   // Get baggage info
//   const onwardBaggage =
//     flight?.Fare?.Onward?.bagCkin?.replace("||", "") || "30 kg";
//   const onwardCabin =
//     flight?.Fare?.Onward?.bagCbin?.replace("||", "") || "7 Kg";
//   const returnBaggage =
//     flight?.Fare?.Return?.bagCkin?.replace("||", "") || "30 KG";
//   const returnCabin =
//     flight?.Fare?.Return?.bagCbin?.replace("||", "") || "7 Kg";

//   // Handle viewing prices
//   const handleViewPrices = async () => {
//     if (showPriceOptions) {
//       setShowPriceOptions(false);
//       setSelectedFare(null);
//       return;
//     }

//     setLoadingPrices(true);
//     try {
//       // For international round trip, verify price for the complete package
//       const onwardFlightId = onwardFirstSegment?.flightID;
//       if (!onwardFlightId || !statusRefdId) {
//         throw new Error("Missing flight ID or reference ID");
//       }

//       const options = await verifyFlightPrice(
//         onwardFlightId,
//         statusRefdId,
//         true // isRoundTrip = true for international
//       );

//       if (options && Array.isArray(options)) {
//         setFareOptions(options);

//         // Select the standard fare by default
//         if (options.length > 0) {
//           setSelectedFare(options[0]);
//         }

//         setShowPriceOptions(true);
//       }
//     } catch (error) {
//       console.error("Error fetching price options:", error);

//       // Create default options if API fails
//       const defaultOptions = [
//         {
//           fareName: "Standard Fare",
//           price: totalPrice,
//           base: basePrice.toString(),
//           tax: taxPrice.toString(),
//           baggage: { cabin: onwardCabin, checkin: onwardBaggage },
//           cancellationFee: "4,275",
//           dateChangeFee: "Contact Airlines",
//           seats: "chargeable",
//           meals: "not available",
//           refundable: "Non-Refundable",
//           ssrInfo: {
//             Onward: {
//               Bagg: [],
//               Meal: [],
//             },
//             Return: {
//               Bagg: [],
//               Meal: [],
//             },
//           },
//         },
//       ];

//       setFareOptions(defaultOptions);
//       setSelectedFare(defaultOptions[0]);
//       setShowPriceOptions(true);
//     } finally {
//       setLoadingPrices(false);
//     }
//   };

//   // Handle fare selection
//   const handleSelectFare = (fare) => {
//     setSelectedFare(fare);
//     // Reset SSR selections when changing fare
//     setSelectedSSROptions({
//       onward: { baggage: null, meal: null },
//       return: { baggage: null, meal: null },
//     });
//   };

//   // Handle SSR option selection
//   const handleSelectSSROption = (flightType, optionType, option) => {
//     setSelectedSSROptions((prev) => ({
//       ...prev,
//       [flightType]: {
//         ...prev[flightType],
//         [optionType]: option,
//       },
//     }));
//   };

//   // Calculate total additional price from SSR options
//   const calculateSSRTotal = () => {
//     let total = 0;

//     if (selectedSSROptions.onward.baggage) {
//       total += parseFloat(selectedSSROptions.onward.baggage.baggAmt || 0);
//     }
//     if (selectedSSROptions.onward.meal) {
//       total += parseFloat(selectedSSROptions.onward.meal.mealAmt || 0);
//     }
//     if (selectedSSROptions.return.baggage) {
//       total += parseFloat(selectedSSROptions.return.baggage.baggAmt || 0);
//     }
//     if (selectedSSROptions.return.meal) {
//       total += parseFloat(selectedSSROptions.return.meal.mealAmt || 0);
//     }

//     return total;
//   };

//   // Get SSR info from selected fare
//   const getSSRInfo = () => {
//     if (!selectedFare || !selectedFare.ssrInfo) {
//       return { Onward: { Bagg: [], Meal: [] }, Return: { Bagg: [], Meal: [] } };
//     }
//     return selectedFare.ssrInfo;
//   };

//   const ssrInfo = getSSRInfo();

//   // Calculate final total
//   const baseFarePrice = selectedFare
//     ? parseInt(selectedFare.price)
//     : totalPrice;
//   const ssrTotal = calculateSSRTotal();
//   const finalTotalPrice = baseFarePrice + ssrTotal;

//   // Toggle SSR sections
//   const toggleSSRSection = (section) => {
//     setExpandedSSRSections((prev) => ({
//       ...prev,
//       [section]: !prev[section],
//     }));
//   };

//   // Render SSR options for a specific flight type
//   const renderSSROptions = (flightType) => {
//     const ssr = ssrInfo[flightType === "onward" ? "Onward" : "Return"];
//     const selected = selectedSSROptions[flightType];
//     const isExpanded = expandedSSRSections[flightType];
//     const flightLabel = flightType === "onward" ? "Onward" : "Return";
//     const flightColor = flightType === "onward" ? "blue" : "green";
//     const baseBaggage = flightType === "onward" ? onwardBaggage : returnBaggage;

//     return (
//       <div className="mt-4 border rounded-lg p-4">
//         <div className="flex justify-between items-center mb-3">
//           <div className="flex items-center">
//             <span
//               className={`px-3 py-1 bg-${flightColor}-100 text-${flightColor}-700 text-sm font-semibold rounded mr-3`}
//             >
//               {flightLabel}
//             </span>
//             <h4 className="font-medium text-gray-800">Extra Services</h4>
//           </div>
//           {(ssr.Bagg?.length > 0 || ssr.Meal?.length > 0) && (
//             <button
//               onClick={() => toggleSSRSection(flightType)}
//               className={`text-${flightColor}-600 hover:text-${flightColor}-800 font-medium flex items-center`}
//             >
//               {isExpanded ? "Hide Options" : "Show Options"}
//               {isExpanded ? (
//                 <FaMinusCircle className="ml-2" />
//               ) : (
//                 <FaPlusCircle className="ml-2" />
//               )}
//             </button>
//           )}
//         </div>

//         {isExpanded && (
//           <div className="space-y-4">
//             {/* Baggage Options */}
//             {ssr.Bagg && ssr.Bagg.length > 0 && (
//               <div>
//                 <h5 className="font-medium text-gray-700 mb-2 flex items-center">
//                   <FaWeightHanging className={`mr-2 text-${flightColor}-500`} />
//                   Extra Baggage (in addition to {baseBaggage})
//                 </h5>
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
//                   {ssr.Bagg.map((baggage, idx) => (
//                     <div
//                       key={`${flightType}-baggage-${idx}`}
//                       className={`border rounded-lg p-3 cursor-pointer transition-all ${
//                         selected.baggage?.baggID === baggage.baggID
//                           ? `border-${flightColor}-500 bg-${flightColor}-50`
//                           : "border-gray-200 hover:bg-gray-50"
//                       }`}
//                       onClick={() =>
//                         handleSelectSSROption(flightType, "baggage", baggage)
//                       }
//                     >
//                       <div className="flex justify-between items-center">
//                         <div className="font-medium text-sm">
//                           {baggage.baggDesc}
//                         </div>
//                         <div className="text-right">
//                           <div className="font-bold text-gray-900">
//                             ₹{parseFloat(baggage.baggAmt).toLocaleString()}
//                           </div>
//                           {selected.baggage?.baggID === baggage.baggID && (
//                             <FaCheckCircle className="text-green-500 mt-1 ml-auto" />
//                           )}
//                         </div>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             )}

//             {/* Meal Options */}
//             {ssr.Meal && ssr.Meal.length > 0 && (
//               <div>
//                 <h5 className="font-medium text-gray-700 mb-2 flex items-center">
//                   <FaHamburger className={`mr-2 text-${flightColor}-500`} />
//                   In-flight Meals
//                 </h5>
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
//                   {ssr.Meal.map((meal, idx) => (
//                     <div
//                       key={`${flightType}-meal-${idx}`}
//                       className={`border rounded-lg p-3 cursor-pointer transition-all ${
//                         selected.meal?.mealID === meal.mealID
//                           ? `border-${flightColor}-500 bg-${flightColor}-50`
//                           : "border-gray-200 hover:bg-gray-50"
//                       }`}
//                       onClick={() =>
//                         handleSelectSSROption(flightType, "meal", meal)
//                       }
//                     >
//                       <div className="flex justify-between items-center">
//                         <div>
//                           <div className="font-medium text-sm">
//                             {meal.mealDesc}
//                           </div>
//                           {meal.mealRef && (
//                             <div className="text-xs text-gray-500 mt-1">
//                               Ref: {meal.mealRef}
//                             </div>
//                           )}
//                         </div>
//                         <div className="text-right">
//                           <div className="font-bold text-gray-900">
//                             ₹{parseFloat(meal.mealAmt).toLocaleString()}
//                           </div>
//                           {selected.meal?.mealID === meal.mealID && (
//                             <FaCheckCircle className="text-green-500 mt-1 ml-auto" />
//                           )}
//                         </div>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             )}
//           </div>
//         )}

//         {/* Selected Options Summary */}
//         {(selected.baggage || selected.meal) && (
//           <div className="mt-4 p-3 bg-gray-50 rounded-lg border">
//             <h5 className="font-medium text-gray-700 mb-2">
//               Selected for {flightLabel}:
//             </h5>
//             <div className="space-y-1 text-sm">
//               {selected.baggage && (
//                 <div className="flex justify-between">
//                   <span className="text-gray-600">
//                     {selected.baggage.baggDesc}:
//                   </span>
//                   <span className="font-medium">
//                     ₹{parseFloat(selected.baggage.baggAmt).toLocaleString()}
//                   </span>
//                 </div>
//               )}
//               {selected.meal && (
//                 <div className="flex justify-between">
//                   <span className="text-gray-600">
//                     {selected.meal.mealDesc}:
//                   </span>
//                   <span className="font-medium">
//                     ₹{parseFloat(selected.meal.mealAmt).toLocaleString()}
//                   </span>
//                 </div>
//               )}
//             </div>
//           </div>
//         )}

//         {!isExpanded && (ssr.Bagg?.length > 0 || ssr.Meal?.length > 0) && (
//           <div className="text-center py-2 text-sm text-gray-500">
//             Click "Show Options" to view available extra services
//           </div>
//         )}
//       </div>
//     );
//   };

//   // Handle booking
//   const handleBookNow = () => {
//     if (!selectedFare) {
//       alert("Please select a fare option first");
//       return;
//     }

//     // Prepare booking data
//     const bookingData = {
//       isRoundTrip: true,
//       isInternational: true,
//       flight: flight,
//       selectedFare: selectedFare,
//       selectedSSROptions: selectedSSROptions,
//       totalPrice: finalTotalPrice,
//       baseFarePrice: baseFarePrice,
//       ssrTotal: ssrTotal,
//       flightID: onwardFirstSegment?.flightID,
//       refID: statusRefdId,
//       onwardFlight: {
//         Flights: { Onward: onwardFlight },
//         Fare: flight.Fare?.Onward,
//       },
//       returnFlight: {
//         Flights: { Onward: returnFlight },
//         Fare: flight.Fare?.Return,
//       },
//     };

//     handleAuthCheck(bookingData);
//   };

//   // Categorize fare options
//   const categorizeFareOptions = () => {
//     const categories = {
//       standard: [],
//       premium: [],
//       meal: [],
//     };

//     fareOptions.forEach((fare) => {
//       if (fare.fareName === "Standard Fare") {
//         categories.standard.push(fare);
//       } else if (fare.fareName.startsWith("Premium Fare")) {
//         categories.premium.push(fare);
//       } else if (fare.fareName.startsWith("Meal Option")) {
//         categories.meal.push(fare);
//       }
//     });

//     return categories;
//   };

//   const fareCategories = categorizeFareOptions();

//   return (
//     <motion.div
//       initial={{ opacity: 0, y: 20 }}
//       animate={{ opacity: 1, y: 0 }}
//       transition={{ duration: 0.3, delay: index * 0.05 }}
//       className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden mb-6 hover:shadow-xl transition-shadow duration-300"
//     >
//       {/* Round Trip Header - International */}
//       <div className="bg-gradient-to-r from-purple-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
//         <div className="flex justify-between items-center">
//           <div className="flex items-center space-x-3">
//             <div className="bg-purple-100 p-2 rounded-full">
//               <FaGlobe className="text-purple-600" />
//             </div>
//             <div>
//               <h3 className="text-lg font-bold text-gray-900">
//                 International Round Trip
//               </h3>
//               <p className="text-lg font-bold text-gray-600">
//                 {onwardAirline} / {returnAirline}
//               </p>
//             </div>
//           </div>
//           <div className="text-right">
//             <div className="text-xl font-bold text-purple-700">
//               ₹{totalPrice.toLocaleString()}
//             </div>
//             <div className="text-sm text-gray-500">Starting from</div>
//           </div>
//         </div>
//       </div>

//       <div className="p-6">
//         {/* Flights Comparison */}
//         <div className="grid md:grid-cols-2 gap-6 mb-6">
//           {/* Onward Flight */}
//           <div className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
//             <div className="flex justify-between items-start mb-3">
//               <div>
//                 <div className="flex items-center space-x-2 mb-1">
//                   <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
//                     Onward
//                   </span>
//                   <span className="text-xs text-gray-500">
//                     {formatApiDateTime
//                       ? formatApiDateTime(onwardFirstSegment?.depDate)?.split(
//                           ","
//                         )[0]
//                       : onwardFirstSegment?.depDate?.slice(6, 8) +
//                         "/" +
//                         onwardFirstSegment?.depDate?.slice(4, 6)}
//                   </span>
//                 </div>
//                 <h4 className="font-semibold text-gray-800">{onwardAirline}</h4>
//               </div>
//             </div>

//             {/* Flight Route */}
//             <div className="flex items-center justify-between mb-3">
//               <div className="text-center">
//                 <div className="text-lg font-bold">
//                   {onwardFirstSegment?.depCode}
//                 </div>
//                 <div className="text-sm text-gray-500">
//                   {formatApiDateTime
//                     ? formatApiDateTime(onwardFirstSegment?.depDate)
//                         ?.split(",")[1]
//                         ?.trim()
//                     : onwardFirstSegment?.depDate?.slice(8, 10) +
//                       ":" +
//                       onwardFirstSegment?.depDate?.slice(10, 12)}
//                 </div>
//               </div>

//               <div className="flex-1 px-4">
//                 <div className="relative">
//                   <div className="flex items-center">
//                     <div className="flex-1 border-t border-gray-300"></div>
//                     <div className="mx-2 relative">
//                       <FaPlane className="text-blue-500 text-sm" />
//                       <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 text-xs text-gray-500 whitespace-nowrap">
//                         {minutesToHm
//                           ? minutesToHm(
//                               getTotalDurationMinutesFunc(onwardFlight)
//                             )
//                           : onwardFlight?.durTotal
//                           ? minutesToHm(onwardFlight.durTotal)
//                           : "N/A"}
//                       </div>
//                     </div>
//                     <div className="flex-1 border-t border-gray-300"></div>
//                   </div>
//                   <div className="text-center text-xs text-gray-600 mt-1">
//                     {getStopsTextFunc(onwardFlight) || "Non-stop"}
//                   </div>
//                 </div>
//               </div>

//               <div className="text-center">
//                 <div className="text-lg font-bold">
//                   {onwardLastSegment?.arrCode}
//                 </div>
//                 <div className="text-sm text-gray-500">
//                   {formatApiDateTime
//                     ? formatApiDateTime(onwardLastSegment?.arrDate)
//                         ?.split(",")[1]
//                         ?.trim()
//                     : onwardLastSegment?.arrDate?.slice(8, 10) +
//                       ":" +
//                       onwardLastSegment?.arrDate?.slice(10, 12)}
//                 </div>
//               </div>
//             </div>

//             {/* Flight Info */}
//             <div className="flex items-center text-sm text-gray-600 mb-4">
//               <FaClock className="mr-1 text-gray-400" />
//               <span>
//                 {minutesToHm
//                   ? minutesToHm(getTotalDurationMinutesFunc(onwardFlight))
//                   : onwardFlight?.durTotal
//                   ? minutesToHm(onwardFlight.durTotal)
//                   : "N/A"}
//               </span>
//               <span className="mx-2">•</span>
//               <span>{onwardFirstSegment?.flightNo}</span>
//               <span className="mx-2">•</span>
//               <span className="flex items-center">
//                 <FaChair className="mr-1 text-gray-400" />
//                 Cabin: {onwardCabin}
//               </span>
//             </div>

//             {/* Baggage Info */}
//             <div className="mb-4 p-3 bg-blue-50 rounded-lg">
//               <div className="flex items-center text-sm">
//                 <FaSuitcase className="mr-2 text-blue-500" />
//                 <span className="font-medium">Baggage Allowance:</span>
//                 <span className="ml-2">{onwardBaggage} Check-in</span>
//               </div>
//             </div>

//             {/* Onward Flight Details Toggle */}
//             <div className="border-t pt-3 mt-3">
//               <button
//                 onClick={() => setExpandedOnwardDetails(!expandedOnwardDetails)}
//                 className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium w-full justify-end"
//               >
//                 {expandedOnwardDetails ? (
//                   <>
//                     Hide Flight Details <FaChevronUp className="ml-1" />
//                   </>
//                 ) : (
//                   <>
//                     View Flight Details <FaChevronDown className="ml-1" />
//                   </>
//                 )}
//               </button>
//             </div>

//             {/* Onward Flight Details */}
//             <AnimatePresence>
//               {expandedOnwardDetails && (
//                 <motion.div
//                   initial={{ opacity: 0, height: 0 }}
//                   animate={{ opacity: 1, height: "auto" }}
//                   exit={{ opacity: 0, height: 0 }}
//                   transition={{ duration: 0.3 }}
//                   className="mt-4 border-t pt-4"
//                 >
//                   <h4 className="text-sm font-semibold text-gray-700 mb-3">
//                     Onward Flight Details
//                   </h4>
//                   <FlightDetails segments={onwardSegments} flight={flight} />
//                 </motion.div>
//               )}
//             </AnimatePresence>
//           </div>

//           {/* Return Flight */}
//           <div className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
//             <div className="flex justify-between items-start mb-3">
//               <div>
//                 <div className="flex items-center space-x-2 mb-1">
//                   <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
//                     Return
//                   </span>
//                   <span className="text-xs text-gray-500">
//                     {formatApiDateTime
//                       ? formatApiDateTime(returnFirstSegment?.depDate)?.split(
//                           ","
//                         )[0]
//                       : returnFirstSegment?.depDate?.slice(6, 8) +
//                         "/" +
//                         returnFirstSegment?.depDate?.slice(4, 6)}
//                   </span>
//                 </div>
//                 <h4 className="font-semibold text-gray-800">{returnAirline}</h4>
//               </div>
//             </div>

//             {/* Flight Route */}
//             <div className="flex items-center justify-between mb-3">
//               <div className="text-center">
//                 <div className="text-lg font-bold">
//                   {returnFirstSegment?.depCode}
//                 </div>
//                 <div className="text-sm text-gray-500">
//                   {formatApiDateTime
//                     ? formatApiDateTime(returnFirstSegment?.depDate)
//                         ?.split(",")[1]
//                         ?.trim()
//                     : returnFirstSegment?.depDate?.slice(8, 10) +
//                       ":" +
//                       returnFirstSegment?.depDate?.slice(10, 12)}
//                 </div>
//               </div>

//               <div className="flex-1 px-4">
//                 <div className="relative">
//                   <div className="flex items-center">
//                     <div className="flex-1 border-t border-gray-300"></div>
//                     <div className="mx-2 relative">
//                       <FaPlane className="text-green-500 text-sm" />
//                       <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 text-xs text-gray-500 whitespace-nowrap">
//                         {minutesToHm
//                           ? minutesToHm(
//                               getTotalDurationMinutesFunc(returnFlight)
//                             )
//                           : returnFlight?.durTotal
//                           ? minutesToHm(returnFlight.durTotal)
//                           : "N/A"}
//                       </div>
//                     </div>
//                     <div className="flex-1 border-t border-gray-300"></div>
//                   </div>
//                   <div className="text-center text-xs text-gray-600 mt-1">
//                     {getStopsTextFunc(returnFlight) || "Non-stop"}
//                   </div>
//                 </div>
//               </div>

//               <div className="text-center">
//                 <div className="text-lg font-bold">
//                   {returnLastSegment?.arrCode}
//                 </div>
//                 <div className="text-sm text-gray-500">
//                   {formatApiDateTime
//                     ? formatApiDateTime(returnLastSegment?.arrDate)
//                         ?.split(",")[1]
//                         ?.trim()
//                     : returnLastSegment?.arrDate?.slice(8, 10) +
//                       ":" +
//                       returnLastSegment?.arrDate?.slice(10, 12)}
//                 </div>
//               </div>
//             </div>

//             {/* Flight Info */}
//             <div className="flex items-center text-sm text-gray-600 mb-4">
//               <FaClock className="mr-1 text-gray-400" />
//               <span>
//                 {minutesToHm
//                   ? minutesToHm(getTotalDurationMinutesFunc(returnFlight))
//                   : returnFlight?.durTotal
//                   ? minutesToHm(returnFlight.durTotal)
//                   : "N/A"}
//               </span>
//               <span className="mx-2">•</span>
//               <span>{returnFirstSegment?.flightNo}</span>
//               <span className="mx-2">•</span>
//               <span className="flex items-center">
//                 <FaChair className="mr-1 text-gray-400" />
//                 Cabin: {returnCabin}
//               </span>
//             </div>

//             {/* Baggage Info */}
//             <div className="mb-4 p-3 bg-green-50 rounded-lg">
//               <div className="flex items-center text-sm">
//                 <FaSuitcase className="mr-2 text-green-500" />
//                 <span className="font-medium">Baggage Allowance:</span>
//                 <span className="ml-2">{returnBaggage} Check-in</span>
//               </div>
//             </div>

//             {/* Return Flight Details Toggle */}
//             <div className="border-t pt-3 mt-3">
//               <button
//                 onClick={() => setExpandedReturnDetails(!expandedReturnDetails)}
//                 className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium w-full justify-end"
//               >
//                 {expandedReturnDetails ? (
//                   <>
//                     Hide Flight Details <FaChevronUp className="ml-1" />
//                   </>
//                 ) : (
//                   <>
//                     View Flight Details <FaChevronDown className="ml-1" />
//                   </>
//                 )}
//               </button>
//             </div>

//             {/* Return Flight Details */}
//             <AnimatePresence>
//               {expandedReturnDetails && (
//                 <motion.div
//                   initial={{ opacity: 0, height: 0 }}
//                   animate={{ opacity: 1, height: "auto" }}
//                   exit={{ opacity: 0, height: 0 }}
//                   transition={{ duration: 0.3 }}
//                   className="mt-4 border-t pt-4"
//                 >
//                   <h4 className="text-sm font-semibold text-gray-700 mb-3">
//                     Return Flight Details
//                   </h4>
//                   <FlightDetails segments={returnSegments} flight={flight} />
//                 </motion.div>
//               )}
//             </AnimatePresence>
//           </div>
//         </div>

//         {/* VIEW PRICES BUTTON */}
//         <div className="flex justify-center mb-6">
//           <button
//             onClick={handleViewPrices}
//             className="px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-colors flex items-center space-x-2"
//           >
//             {loadingPrices ? (
//               <>
//                 <FaSpinner className="animate-spin" />
//                 <span>Loading Prices...</span>
//               </>
//             ) : showPriceOptions ? (
//               "Hide Prices & Options"
//             ) : (
//               "View Prices & Book"
//             )}
//           </button>
//         </div>

//         {/* Price Options and SSR Selection */}
//         <AnimatePresence>
//           {showPriceOptions && (
//             <motion.div
//               initial={{ opacity: 0, height: 0 }}
//               animate={{ opacity: 1, height: "auto" }}
//               exit={{ opacity: 0, height: 0 }}
//               transition={{ duration: 0.3 }}
//               className="mt-4 mb-6"
//             >
//               <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
//                 <h4 className="text-lg font-bold text-gray-900 mb-4 text-center">
//                   Select Your Fare Package
//                 </h4>

//                 {loadingPrices ? (
//                   <div className="text-center py-8">
//                     <FaSpinner className="animate-spin text-purple-500 text-3xl mx-auto mb-4" />
//                     <p className="text-gray-600">Loading fare options...</p>
//                   </div>
//                 ) : (
//                   <>
//                     {/* Standard Fare */}
//                     {fareCategories.standard.length > 0 && (
//                       <div className="mb-6">
//                         <h5 className="font-bold text-gray-800 mb-3 flex items-center">
//                           <FaTag className="mr-2 text-purple-500" />
//                           Standard Fares
//                         </h5>
//                         <div className="space-y-3">
//                           {fareCategories.standard.map((fare, idx) => (
//                             <div
//                               key={`standard-${idx}`}
//                               className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
//                                 selectedFare?.fareName === fare.fareName
//                                   ? "border-purple-500 bg-purple-50"
//                                   : "border-gray-200 bg-white hover:bg-gray-50"
//                               }`}
//                               onClick={() => handleSelectFare(fare)}
//                             >
//                               <div className="flex justify-between items-center">
//                                 <div>
//                                   <div className="font-bold text-gray-900">
//                                     {fare.fareName}
//                                   </div>
//                                   <div className="text-sm text-gray-600 mt-1">
//                                     <span className="bg-gray-100 px-2 py-1 rounded mr-2">
//                                       {fare.refundable}
//                                     </span>
//                                     <span className="bg-gray-100 px-2 py-1 rounded">
//                                       {fare.meals}
//                                     </span>
//                                   </div>
//                                 </div>
//                                 <div className="text-right">
//                                   <div className="text-xl font-bold text-purple-700">
//                                     ₹{parseInt(fare.price).toLocaleString()}
//                                   </div>
//                                   <div className="text-sm text-gray-500">
//                                     Base: ₹{fare.base} + Tax: ₹{fare.tax}
//                                   </div>
//                                 </div>
//                               </div>
//                               <div className="mt-3 text-sm text-gray-600">
//                                 <div className="flex items-center">
//                                   <FaSuitcase className="mr-2" />
//                                   Baggage: {onwardBaggage} (Onward) /{" "}
//                                   {returnBaggage} (Return)
//                                 </div>
//                                 <div className="flex items-center mt-1">
//                                   <FaExchangeAlt className="mr-2" />
//                                   Cancellation: ₹{fare.cancellationFee}
//                                 </div>
//                               </div>
//                             </div>
//                           ))}
//                         </div>
//                       </div>
//                     )}

//                     {/* Premium Fares */}
//                     {fareCategories.premium.length > 0 && (
//                       <div className="mb-6">
//                         <h5 className="font-bold text-gray-800 mb-3 flex items-center">
//                           <FaWeightHanging className="mr-2 text-blue-500" />
//                           Premium Fares (Extra Baggage)
//                         </h5>
//                         <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
//                           {fareCategories.premium.map((fare, idx) => (
//                             <div
//                               key={`premium-${idx}`}
//                               className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
//                                 selectedFare?.fareName === fare.fareName
//                                   ? "border-blue-500 bg-blue-50"
//                                   : "border-gray-200 bg-white hover:bg-gray-50"
//                               }`}
//                               onClick={() => handleSelectFare(fare)}
//                             >
//                               <div className="font-bold text-gray-900 mb-2">
//                                 {fare.fareName}
//                               </div>
//                               <div className="text-sm text-gray-600 mb-2">
//                                 Includes extra baggage allowance
//                               </div>
//                               <div className="text-lg font-bold text-blue-700">
//                                 ₹{parseInt(fare.price).toLocaleString()}
//                               </div>
//                               {fare.baggageOption && (
//                                 <div className="mt-2 text-xs text-gray-500">
//                                   {fare.baggageOption.baggDesc}
//                                 </div>
//                               )}
//                             </div>
//                           ))}
//                         </div>
//                       </div>
//                     )}

//                     {/* Meal Options */}
//                     {fareCategories.meal.length > 0 && (
//                       <div className="mb-6">
//                         <h5 className="font-bold text-gray-800 mb-3 flex items-center">
//                           <FaHamburger className="mr-2 text-green-500" />
//                           Meal Options
//                         </h5>
//                         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
//                           {fareCategories.meal.slice(0, 6).map((fare, idx) => (
//                             <div
//                               key={`meal-${idx}`}
//                               className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
//                                 selectedFare?.fareName === fare.fareName
//                                   ? "border-green-500 bg-green-50"
//                                   : "border-gray-200 bg-white hover:bg-gray-50"
//                               }`}
//                               onClick={() => handleSelectFare(fare)}
//                             >
//                               <div className="font-bold text-gray-900 mb-2">
//                                 {fare.fareName}
//                               </div>
//                               <div className="text-sm text-gray-600 mb-2 truncate">
//                                 {fare.mealOption?.mealDesc || "Meal included"}
//                               </div>
//                               <div className="text-lg font-bold text-green-700">
//                                 ₹{parseInt(fare.price).toLocaleString()}
//                               </div>
//                             </div>
//                           ))}
//                         </div>
//                         {fareCategories.meal.length > 6 && (
//                           <div className="text-center mt-3 text-sm text-gray-500">
//                             + {fareCategories.meal.length - 6} more meal options
//                             available
//                           </div>
//                         )}
//                       </div>
//                     )}

//                     {/* SSR Options Section (only shown when Standard Fare is selected) */}
//                     {selectedFare &&
//                       selectedFare.fareName === "Standard Fare" && (
//                         <div className="mt-6 pt-6 border-t">
//                           <h4 className="text-lg font-bold text-gray-900 mb-4">
//                             Add Extra Services to Standard Fare
//                           </h4>
//                           <div className="space-y-6">
//                             {renderSSROptions("onward")}
//                             {renderSSROptions("return")}
//                           </div>
//                         </div>
//                       )}

//                     {/* Selected Package Summary */}
//                     {selectedFare && (
//                       <div className="mt-6 bg-white rounded-lg p-4 border border-purple-300">
//                         <div className="flex justify-between items-center mb-3">
//                           <div>
//                             <div className="font-bold text-gray-900">
//                               Selected Package
//                             </div>
//                             <div className="text-sm text-gray-600">
//                               {selectedFare.fareName}
//                             </div>
//                           </div>
//                           <div className="text-right">
//                             <div className="text-2xl font-bold text-purple-700">
//                               ₹{finalTotalPrice.toLocaleString()}
//                             </div>
//                             {ssrTotal > 0 && (
//                               <div className="text-sm text-gray-500">
//                                 Fare: ₹{baseFarePrice.toLocaleString()} +
//                                 Extras: ₹{ssrTotal.toLocaleString()}
//                               </div>
//                             )}
//                           </div>
//                         </div>

//                         {/* Selected SSR Summary */}
//                         {(selectedSSROptions.onward.baggage ||
//                           selectedSSROptions.onward.meal ||
//                           selectedSSROptions.return.baggage ||
//                           selectedSSROptions.return.meal) && (
//                           <div className="mt-4 pt-4 border-t">
//                             <div className="font-medium text-gray-700 mb-2">
//                               Extra Services:
//                             </div>
//                             <div className="space-y-1 text-sm">
//                               {selectedSSROptions.onward.baggage && (
//                                 <div className="flex justify-between">
//                                   <span>
//                                     <span className="px-1 py-0.5 bg-blue-100 text-blue-700 text-xs rounded mr-1">
//                                       Onward
//                                     </span>
//                                     {selectedSSROptions.onward.baggage.baggDesc}
//                                   </span>
//                                   <span className="font-medium">
//                                     ₹
//                                     {parseFloat(
//                                       selectedSSROptions.onward.baggage.baggAmt
//                                     ).toLocaleString()}
//                                   </span>
//                                 </div>
//                               )}
//                               {selectedSSROptions.onward.meal && (
//                                 <div className="flex justify-between">
//                                   <span>
//                                     <span className="px-1 py-0.5 bg-blue-100 text-blue-700 text-xs rounded mr-1">
//                                       Onward
//                                     </span>
//                                     {selectedSSROptions.onward.meal.mealDesc}
//                                   </span>
//                                   <span className="font-medium">
//                                     ₹
//                                     {parseFloat(
//                                       selectedSSROptions.onward.meal.mealAmt
//                                     ).toLocaleString()}
//                                   </span>
//                                 </div>
//                               )}
//                               {selectedSSROptions.return.baggage && (
//                                 <div className="flex justify-between">
//                                   <span>
//                                     <span className="px-1 py-0.5 bg-green-100 text-green-700 text-xs rounded mr-1">
//                                       Return
//                                     </span>
//                                     {selectedSSROptions.return.baggage.baggDesc}
//                                   </span>
//                                   <span className="font-medium">
//                                     ₹
//                                     {parseFloat(
//                                       selectedSSROptions.return.baggage.baggAmt
//                                     ).toLocaleString()}
//                                   </span>
//                                 </div>
//                               )}
//                               {selectedSSROptions.return.meal && (
//                                 <div className="flex justify-between">
//                                   <span>
//                                     <span className="px-1 py-0.5 bg-green-100 text-green-700 text-xs rounded mr-1">
//                                       Return
//                                     </span>
//                                     {selectedSSROptions.return.meal.mealDesc}
//                                   </span>
//                                   <span className="font-medium">
//                                     ₹
//                                     {parseFloat(
//                                       selectedSSROptions.return.meal.mealAmt
//                                     ).toLocaleString()}
//                                   </span>
//                                 </div>
//                               )}
//                             </div>
//                           </div>
//                         )}

//                         {/* Book Now Button */}
//                         <div className="mt-4">
//                           <button
//                             onClick={handleBookNow}
//                             className="w-full py-4 rounded-lg font-bold text-xl transition-all duration-300 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl"
//                           >
//                             BOOK NOW - ₹{finalTotalPrice.toLocaleString()}
//                           </button>
//                         </div>
//                       </div>
//                     )}
//                   </>
//                 )}
//               </div>
//             </motion.div>
//           )}
//         </AnimatePresence>
//       </div>
//     </motion.div>
//   );
// };

// export default InternationalRoundTripFlightCard;
